<template>
  <div class="payment-type-select" v-if="types.length">
    <div class="payment-type-btns" role="group" aria-label="Forma de pagamento">
      <button
        v-for="type in types"
        :key="type.id"
        type="button"
        class="payment-type-btn"
        :class="{ 'payment-type-btn--active': selectedType === type.id }"
        @click="selectType(type.id)"
      >
        {{ type.label }}
      </button>
    </div>
  </div>
  <p v-else class="checkout-payment-missing">
    Nenhuma forma de pagamento disponível no momento.
  </p>
</template>

<script>
import {
  getPaymentTypes,
  resolvePaymentMethod,
  getCardProviders,
  pickBalancedCardProvider,
  peekCardProviderBalance
} from '../utils/paymentProviderBalance';
import { checkoutLog } from '../utils/checkoutDebug';

export default {
  name: 'PaymentTypeSelect',
  props: {
    basket: {
      type: Object,
      required: true
    },
    paymentMethod: {
      type: String,
      required: true
    },
    isSubscription: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      selectedType: '',
      failedProviders: []
    };
  },
  computed: {
    types() {
      return getPaymentTypes({
        currency: this.basket.currency_code,
        isSubscription: this.isSubscription
      });
    }
  },
  watch: {
    types: {
      handler(types) {
        if (!types.length) {
          this.selectedType = '';
          return;
        }
        if (this.selectedType && !types.some(t => t.id === this.selectedType)) {
          this.selectedType = '';
          this.$emit('select-payment', '');
        }
        this.tryAutoSelectSingleType();
      },
      immediate: true
    },
    isSubscription() {
      this.$nextTick(() => this.tryAutoSelectSingleType());
    },
    paymentMethod(method) {
      if (!method) {
        this.$nextTick(() => this.tryAutoSelectSingleType());
      }
    }
  },
  mounted() {
    checkoutLog(
      'payment:types-ready',
      {
        types: this.types,
        isSubscription: this.isSubscription,
        balance: peekCardProviderBalance({
          isSubscription: this.isSubscription,
          currency: this.basket.currency_code
        })
      },
      this.$route
    );
    this.$EventBus.$on('paymentProviderFailed', this.onProviderFailed);
    this.$EventBus.$on('resetPaymentType', this.onResetPaymentType);
    this.$nextTick(() => this.tryAutoSelectSingleType());
  },
  beforeDestroy() {
    this.$EventBus.$off('paymentProviderFailed', this.onProviderFailed);
    this.$EventBus.$off('resetPaymentType', this.onResetPaymentType);
  },
  methods: {
    tryAutoSelectSingleType() {
      const types = this.types;
      if (types.length !== 1 || this.paymentMethod) {
        return;
      }

      const onlyTypeId = types[0].id;
      if (this.selectedType === onlyTypeId) {
        return;
      }

      checkoutLog(
        'payment:auto-select-single',
        { typeId: onlyTypeId, isSubscription: this.isSubscription },
        this.$route
      );
      this.selectType(onlyTypeId);
    },
    selectType(typeId) {
      checkoutLog('payment:type-click', { typeId, isSubscription: this.isSubscription }, this.$route);
      this.selectedType = typeId;
      if (typeId === 'card') {
        this.failedProviders = [];
      }
      const isSubscription = this.isSubscription;
      let method = resolvePaymentMethod(typeId, {
        isSubscription,
        currency: this.basket.currency_code,
        exclude: this.failedProviders
      });
      if (isSubscription && method && (method === 'card' || method === 'card-iugu')) {
        method = resolvePaymentMethod(typeId, {
          isSubscription: true,
          currency: this.basket.currency_code,
          exclude: [...this.failedProviders, 'card', 'card-iugu']
        });
      }
      if (method) {
        checkoutLog(
          'payment:type-resolved',
          {
            typeId,
            method,
            balance: peekCardProviderBalance({
              isSubscription: this.isSubscription,
              currency: this.basket.currency_code,
              exclude: this.failedProviders
            })
          },
          this.$route
        );
        this.$emit('select-payment', method);
      } else {
        checkoutLog('payment:type-unresolved', { typeId }, this.$route);
      }
    },
    onProviderFailed(failedMethod) {
      if (!failedMethod || this.selectedType !== 'card') {
        return;
      }
      if (this.failedProviders.indexOf(failedMethod) === -1) {
        this.failedProviders.push(failedMethod);
      }
      const next = pickBalancedCardProvider(
        getCardProviders({
          isSubscription: this.isSubscription,
          currency: this.basket.currency_code
        }),
        this.failedProviders
      );
      if (next) {
        this.$vs.notify({
          time: 4000,
          text: 'Tentando outra operadora…',
          iconPack: 'feather',
          icon: 'icon-refresh-cw',
          color: 'warning'
        });
        this.$EventBus.$emit('changePaymentMethod', next);
      }
    },
    onResetPaymentType() {
      this.selectedType = '';
      this.failedProviders = [];
      this.$nextTick(() => this.tryAutoSelectSingleType());
    }
  }
};
</script>

<style lang="scss" scoped>
.payment-type-select {
  margin-bottom: 0.25rem;
}

.checkout-payment-missing {
  margin: 0.5rem 0 0;
  color: #8a6a3b;
  font-size: 0.9rem;
}

.payment-type-btns {
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
}

.payment-type-btn {
  flex: 1 1 0;
  min-width: 104px;
  max-width: 180px;
  padding: 0.7rem 1rem;
  border-radius: 12px;
  border: 1.5px solid rgba(92, 52, 152, 0.18);
  background: linear-gradient(180deg, #fff 0%, #faf8fd 100%);
  color: #4a4258;
  font-size: 0.92rem;
  font-weight: 650;
  letter-spacing: 0.01em;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(49, 28, 84, 0.05);
  transition: border-color 0.18s ease, background 0.18s ease, color 0.18s ease, box-shadow 0.18s ease,
    transform 0.18s ease;

  &:hover {
    border-color: #5c3498;
    transform: translateY(-1px);
    box-shadow: 0 8px 18px rgba(92, 52, 152, 0.12);
  }

  &--active {
    border-color: #5c3498;
    background: linear-gradient(145deg, #5c3498, #7348b0);
    color: #fff;
    box-shadow: 0 10px 22px rgba(92, 52, 152, 0.28);

    &:hover {
      color: #fff;
      border-color: #5c3498;
    }
  }
}
</style>
